import type { DailyRangeEntry } from '../lib/api';

// WeeklyMacroChart — 7 vertical bars per macro showing the daily consumed
// value vs. the target line. The bar height encodes consumed; the dashed
// horizontal line is the target. Days without a daily_log row show the bare
// grey track — that empty column is the intended "nothing logged" read.
//
// Layout: 4 rows (kcal / protein / fat / carbs) × 7 columns (one per day).
// Each row is independent so the visual scale is per-macro — protein bars
// near 100% won't look flat next to kcal bars near 100%.

const BAR_AREA_HEIGHT = 32;

export function WeeklyMacroChart({
  entries,
}: {
  entries: DailyRangeEntry[];
}) {
  if (entries.length === 0) {
    return (
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-faint py-2 text-center">
        No data
      </p>
    );
  }
  const headers = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const rows: { label: string; key: 'kcal' | 'protein' | 'fat' | 'carbs'; unit: string }[] = [
    { label: 'kcal', key: 'kcal', unit: 'kcal' },
    { label: 'protein', key: 'protein', unit: 'g' },
    { label: 'fat', key: 'fat', unit: 'g' },
    { label: 'carbs', key: 'carbs', unit: 'g' },
  ];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-7 gap-1 pl-12 text-center">
        {entries.slice(0, 7).map((e, i) => (
          <span
            key={e.date}
            className="font-mono text-[9px] uppercase tracking-[0.16em] text-fg-faint"
          >
            {headers[i] ?? '?'}
          </span>
        ))}
      </div>
      {rows.map((row) => (
        <div key={row.key} className="flex items-end gap-1">
          <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-fg-faint w-11 shrink-0 pb-1">
            {row.label}
          </span>
          <div
            className="grid grid-cols-7 gap-1 flex-1 relative"
            style={{ height: BAR_AREA_HEIGHT }}
          >
            {entries.slice(0, 7).map((e) => {
              const pair = e.macros[row.key];
              const ratio = pair.target > 0 ? Math.min(pair.consumed / pair.target, 1.3) : 0;
              const isOver = pair.target > 0 && pair.consumed > pair.target * 1.05;
              const heightPct = (ratio / 1.3) * 100;
              return (
                <div
                  key={e.date}
                  className="relative bg-bg-sunken rounded-[3px] overflow-hidden"
                  title={`${e.date}: ${pair.consumed}/${pair.target}${row.unit}`}
                >
                  <div
                    className={`absolute inset-x-0 bottom-0 transition-[height] duration-300 ease-out ${
                      isOver ? 'bg-warn-mid' : 'bg-good-mid'
                    }`}
                    style={{ height: `${heightPct}%` }}
                  />
                </div>
              );
            })}
            {/* Goal line at 100% target = 76.9% of the visible track */}
            <div
              aria-hidden
              className="absolute left-0 right-0 border-t border-dashed border-fg-faint pointer-events-none"
              style={{ top: `${(0.3 / 1.3) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
